<script setup lang="ts">
	import { useForm, useField } from 'vee-validate';
	import * as yup from 'yup';

	import type { CalcItem } from '@/types';

	interface CalcItemProps {
		idx: number;
		item: CalcItem;
		canDelete: boolean;
	}

	const props = defineProps<CalcItemProps>();

	const emit = defineEmits(['deleteCargoItem', 'update:item']);

	const MESSAGES = {
		requiredLength: 'Введите длину',
		requiredWidth: 'Введите ширину',
		requiredHeight: 'Введите высоту',
		requiredWeight: 'Введите вес',
		requiredCount: 'Введите количество мест',
	};

	const numberIntRule = (msg: string) =>
		yup.number().integer('Введите целое число').positive(msg).required(msg).typeError('Не число');

	const numberRule = (msg: string) => yup.number().positive(msg).required(msg).typeError('Не число');

	const validationSchema = yup.object({
		length: numberIntRule(MESSAGES.requiredLength),
		width: numberIntRule(MESSAGES.requiredWidth),
		height: numberIntRule(MESSAGES.requiredHeight),
		weight: numberRule(MESSAGES.requiredWeight),
		count: numberIntRule(MESSAGES.requiredCount),
	});

	const { validate, meta, setValues } = useForm({ validationSchema, initialValues: props.item });

	defineExpose({
		validate,
		meta,
	});

	const formFields = ref({
		length: {
			label: 'Длина (мм)',
			name: 'length',
			placeholder: '999 999',
			'required-field': true,
			type: 'number',
			iconName: 'length',
			min: 0,
			field: useField<number>('length'),
		},
		width: {
			label: 'Ширина (мм)',
			name: 'width',
			placeholder: '999 999',
			'required-field': true,
			type: 'number',
			iconName: 'width',
			field: useField<number>('width'),
		},
		height: {
			label: 'Высота (мм)',
			name: 'height',
			placeholder: '999 999',
			'required-field': true,
			type: 'number',
			iconName: 'height',
			field: useField<number>('height'),
		},
		weight: {
			label: 'Вес (кг)',
			name: 'mail',
			placeholder: '999 999',
			'required-field': true,
			type: 'number',
			iconName: 'weight2',
			field: useField<number>('weight'),
		},
		count: {
			label: 'Количество',
			name: 'count',
			placeholder: '999 999',
			'required-field': true,
			type: 'number',
			field: useField<number>('count'),
		},
	});

	const changeItem = () => {
		emit('update:item', {
			length: formFields.value?.length?.field.value,
			width: formFields.value?.width?.field.value,
			height: formFields.value?.height?.field.value,
			weight: formFields.value?.weight?.field.value,
			count: formFields.value?.count?.field.value,
			goods: props.item.goods,
		});
	};

	const deleteItem = (idx: number) => {
		emit('deleteCargoItem', idx);
	};

	watch(
		() => props.item,
		() => {
			setValues({
				length: props.item.length,
				width: props.item.width,
				height: props.item.height,
				weight: props.item.weight,
				count: props.item.count,
				goods: props.item.goods,
			});
		}
	);
</script>

<template>
	<div class="card-dimensions">
		<div class="card-dimensions__header">
			<div class="card-dimensions__title">
				<div class="card-dimensions__title-number">{{ idx + 1 }}</div>
				<div>Данные груза</div>
			</div>
			<button
				v-if="canDelete"
				type="button"
				class="button button--white card-dimensions__delete"
				@click="deleteItem(idx)"
			>
				<Icon name="local:trash" />
			</button>
		</div>
		<div class="card-dimensions__inputs-block">
			<UiInputText
				:id="`length_${idx}`"
				v-bind="formFields.length"
				v-model="formFields.length.field.value"
				:error="formFields.length.field.errorMessage"
				@change="changeItem"
			/>
			<UiInputText
				:id="`width_${idx}`"
				v-bind="formFields.width"
				v-model="formFields.width.field.value"
				:error="formFields.width.field.errorMessage"
				@change="changeItem"
			/>
			<UiInputText
				:id="`height_${idx}`"
				v-bind="formFields.height"
				v-model="formFields.height.field.value"
				:error="formFields.height.field.errorMessage"
				@change="changeItem"
			/>
			<UiInputText
				:id="`weight_${idx}`"
				v-bind="formFields.weight"
				v-model="formFields.weight.field.value"
				:error="formFields.weight.field.errorMessage"
				@change="changeItem"
			/>
			<UiInputWithButton
				:id="`count_${idx}`"
				v-bind="formFields.count"
				v-model="formFields.count.field.value"
				:error="formFields.count.field.errorMessage"
				@change="changeItem"
			/>
		</div>
	</div>
</template>

<style lang="scss" scoped>
	.card-dimensions {
		padding: 20px 15px 15px;
		border-radius: 20px;
		box-shadow: var(--box-shadow);
	}
	.card-dimensions__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 15px;
		.card-dimensions__title {
			display: flex;
			align-items: center;
			font-size: 20px;
			font-weight: 700;
		}
		.card-dimensions__title-number {
			display: flex;
			justify-content: center;
			align-items: center;
			height: 26px;
			min-width: 26px;
			padding: 0px 4px;
			border-radius: 13px;
			margin-right: 10px;
			font-size: 16px;
			font-weight: 700;
			color: var(--color-white);
			background-color: var(--color-primary);
		}
		.card-dimensions__delete {
			height: 25px;
			width: 25px;
			display: flex;
			padding: 0;
		}
	}
	.card-dimensions__inputs-block {
		display: grid;
		grid-template-columns: 1fr;
		gap: 20px;
	}

	@media (min-width: 1240px) {
		.card-dimensions {
			padding: 20px;
		}
		.card-dimensions__header {
			margin-bottom: 20px;
			.card-dimensions__title {
				font-size: 24px;
			}
			.card-dimensions__title-number {
				height: 31px;
				min-width: 31px;
				padding: 0px 4px;
				border-radius: 16px;
				margin-right: 12px;
				font-size: 20px;
			}
			.card-dimensions__delete {
				height: 31px;
				width: 31px;
			}
		}
		.card-dimensions__inputs-block {
			grid-template-columns: repeat(5, 1fr);
			gap: 20px;
		}
	}
</style>
